import { useAuth } from "common";
import { FormikProvider, useFormik } from "formik";
import { OTPInput, REGEXP_ONLY_DIGITS } from "input-otp";
import { startTransition, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Location,
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router";
import {
  Button,
  GetRef,
  Separator,
  Text,
  View,
  XStack,
  YStack,
} from "tamagui";
import { GradientButton } from "tamagui-config";

import { FormFieldContainer } from "../components/FormFieldContainer";
import { FormikInput } from "../components/FormikInput";
import { GlowCircle } from "../components/GlowCircle";
import { Input } from "../components/Input";
import useFieldValidationSchema from "../hooks/useFieldValidationSchema";
import { useShowToast } from "../hooks/useShowToast";

type ResetPasswordFormValues = {
  code: string;
  newPassword: string;
  repeatedNewPassword: string;
};

const ResetPassword = () => {
  const { forgotPassword, forgotPasswordSubmit } = useAuth();
  const { state } = useLocation() as Location<{ email: string } | undefined>;
  const [searchParams, setSearchParams] = useSearchParams();
  const { resetPasswordValidationSchema } = useFieldValidationSchema();
  const navigate = useNavigate();
  const newPasswordInputRef = useRef<GetRef<typeof Input>>(null);
  const { showErrorToast, showSuccessToast } = useShowToast();
  const { t } = useTranslation();
  const [isResending, setIsResending] = useState(false);
  // The email link carries ?code=&email=; the "Send code" step passes the
  // email in router state. Either way the user already told us who they are.
  const email = searchParams.get("email") ?? state?.email;
  const code = searchParams.get("code");

  const parseError = (error: any) => {
    switch (error?.name) {
      case "CodeMismatchException":
      case "ExpiredCodeException":
        return t("resetPassword.invalidCodeError");
      case "LimitExceededException":
        return t("resetPassword.tooManyAttemptsError");
      case "InvalidPasswordException":
        // Cognito names the rule that failed, e.g. "must have symbol characters".
        return error.message;
      default:
        return t("resetPassword.unknownError");
    }
  };

  const onSubmit = async ({ code, newPassword }: ResetPasswordFormValues) => {
    try {
      await forgotPasswordSubmit(email ?? "", code, newPassword);
      navigate("/reset-password-confirmation");
    } catch (error) {
      showErrorToast(parseError(error));
    }
  };

  const onResendPress = async () => {
    setIsResending(true);
    try {
      await forgotPassword(email ?? "");
      showSuccessToast(t("resetPassword.codeResent", { email }));
      // The old code is dead now: drop it so the copy asks for the new one.
      formik.setFieldValue("code", "");
      if (code) setSearchParams({ email: email ?? "" }, { replace: true });
    } catch (error) {
      showErrorToast(
        (error as any)?.name === "LimitExceededException"
          ? t("resetPassword.tooManyAttemptsError")
          : t("resetPassword.resendError"),
      );
    } finally {
      setIsResending(false);
    }
  };

  const formik = useFormik<ResetPasswordFormValues>({
    initialValues: {
      code: code ?? "",
      newPassword: "",
      repeatedNewPassword: "",
    },
    validationSchema: resetPasswordValidationSchema,
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit,
  });

  // Opened without an email (typed URL, old bookmark): start the flow over
  // rather than ask for it here.
  if (!email) return <Navigate to="/forgot-password" replace />;

  return (
    <View
      position="relative"
      minHeight="100vh"
      alignItems="center"
      justifyContent="center"
      backgroundColor="black"
      $xs={{ padding: "$3.5" }}
    >
      <View position="absolute" width="100%" height="100%">
        <GlowCircle />
      </View>

      <YStack
        gap="$6"
        width={473}
        maxWidth="100%"
        padding="$7"
        $xs={{ padding: "$4" }}
        backgroundColor="$transparentBorder"
        borderWidth={2}
        borderColor="$transparentBorder"
        borderRadius="$8"
      >
        <Text fontWeight="bold" color="$white12" textAlign="center">
          {t("resetPassword.title")}
        </Text>

        <Separator />

        <FormikProvider value={formik}>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (!formik.isSubmitting) formik.handleSubmit();
            }}
          >
            <YStack gap="$3.5" padding="$3.5">
              <YStack gap="$2.5" paddingHorizontal="$5" paddingBottom="$3.5">
                <Text
                  fontSize="$8"
                  fontWeight="bold"
                  color="$white12"
                  textAlign="center"
                >
                  {t(
                    code
                      ? "resetPassword.formTitleFromLink"
                      : "resetPassword.formTitle",
                  )}
                </Text>
                <Text color="$white10" textAlign="center">
                  {t(
                    code
                      ? "resetPassword.formDescriptionFromLink"
                      : "resetPassword.formDescription",
                    { email },
                  )}
                </Text>
              </YStack>

              <FormFieldContainer
                required
                error={formik.touched.code && !!formik.errors.code}
                helperText={
                  formik.touched.code && formik.errors.code
                    ? formik.errors.code
                    : undefined
                }
              >
                <OTPInput
                  value={formik.values.code}
                  autoFocus
                  pattern={REGEXP_ONLY_DIGITS}
                  maxLength={6}
                  render={({ slots, isHovering }) => (
                    <XStack gap="$3.5" $xs={{ gap: "$2" }}>
                      {slots.map(
                        ({ char, placeholderChar, isActive }, index) => (
                          <View key={index} flexGrow={1} flexBasis={0}>
                            <Input // This Input component is just for display purposes
                              readOnly
                              width="100%"
                              height={64}
                              textAlign="center"
                              value={char ?? ""}
                              placeholder={placeholderChar ?? ""}
                              error={
                                formik.touched.code && !!formik.errors.code
                              }
                              hovered={isHovering}
                              focused={isActive}
                            />
                          </View>
                        ),
                      )}
                    </XStack>
                  )}
                  onChange={formik.handleChange("code")}
                  onBlur={formik.handleBlur("code")}
                  onComplete={() => newPasswordInputRef.current?.focus()}
                />
              </FormFieldContainer>

              <FormikInput
                ref={newPasswordInputRef}
                fieldName="newPassword"
                placeholder={t("resetPassword.newPasswordPlaceholder")}
                autoCapitalize="none"
                isPassword
              />
              <FormikInput
                fieldName="repeatedNewPassword"
                placeholder={t("resetPassword.repeatedNewPasswordPlaceholder")}
                autoCapitalize="none"
                isPassword
              />

              <GradientButton
                rounded
                width="100%"
                loading={formik.isSubmitting}
                disabled={formik.isSubmitting || isResending}
              >
                {t("resetPassword.submitButton")}
              </GradientButton>

              <XStack justifyContent="center" flexWrap="wrap" gap="$2">
                <Button
                  chromeless
                  disabled={isResending || formik.isSubmitting}
                  onPress={(event) => {
                    event.preventDefault();
                    // A Tamagui Button disabled in the same render as its own
                    // press re-renders forever (tamagui#3055, see
                    // GradientButton). Let the press settle first.
                    startTransition(() => {
                      onResendPress();
                    });
                  }}
                >
                  {t("resetPassword.resendCode")}
                </Button>
                <Button
                  chromeless
                  onPress={(event) => {
                    event.preventDefault();
                    navigate("/forgot-password", { replace: true });
                  }}
                >
                  {t("resetPassword.differentEmail")}
                </Button>
              </XStack>
            </YStack>
          </form>
        </FormikProvider>
      </YStack>
    </View>
  );
};

export default ResetPassword;
